import { Delegate } from './delegate.js';

if (!customElements.get('variant-stock-select')) {
  customElements.define(
    'variant-stock-select',
    class VariantStockSelect extends HTMLElement {
      #delegate = null;
      #trigger = null;
      #panel = null;

      connectedCallback() {
        /* Section re-renders move this node, which re-runs connectedCallback -- bail out so a
           second set of listeners can't stack up on the same trigger. */
        if (this.#delegate) return;

        this.#trigger = this.querySelector('[data-action="toggle-stock-panel"]');
        this.#panel = this.querySelector('.variant-stock__panel');
        if (!this.#trigger || !this.#panel) return;

        this.#delegate = new Delegate(this);
        this.#delegate.on('click', '[data-action="toggle-stock-panel"]', this.#onTriggerClick);
        this.#delegate.on('click', '[data-action="select-stock-option"]', this.#onItemClick);
        this.#delegate.on(
          'keydown',
          '[data-action="toggle-stock-panel"], [data-action="select-stock-option"]',
          this.#onKeydown
        );
      }

      disconnectedCallback() {
        this.#delegate?.destroy();
        this.#delegate = null;
        document.removeEventListener('click', this.#onDocumentClick);
        this.#panel?.removeEventListener('transitionend', this.#onPanelTransitionEnd);
        this.#trigger = null;
        this.#panel = null;
      }

      get #isOpen() {
        return this.#trigger.getAttribute('aria-expanded') === 'true';
      }

      #open() {
        // Drop any pending close-transition handler so a stale event can't re-hide a panel
        // that was reopened before its closing animation finished.
        this.#panel.removeEventListener('transitionend', this.#onPanelTransitionEnd);

        this.#trigger.setAttribute('aria-expanded', 'true');
        this.#panel.hidden = false;
        // Force layout so removing `hidden` and adding `is-open` don't get coalesced into a
        // single paint, which would skip the transition.
        void this.#panel.offsetHeight;
        this.#panel.classList.add('is-open');
        document.addEventListener('click', this.#onDocumentClick);
      }

      #close() {
        this.#trigger.setAttribute('aria-expanded', 'false');
        this.#panel.classList.remove('is-open');
        document.removeEventListener('click', this.#onDocumentClick);
        this.#panel.addEventListener('transitionend', this.#onPanelTransitionEnd);
      }

      #onTriggerClick = () => {
        this.#isOpen ? this.#close() : this.#open();
      };

      /**
       * @param {MouseEvent} event
       * @param {HTMLElement} item
       */
      #onItemClick = (event, item) => {
        const input = document.getElementById(item.dataset.inputId);
        this.#close();
        // Connected-product entries carry no input -- connected-products.js handles those, so
        // closing the panel is all this element owes them. Same for an unchanged selection,
        // which re-renders nothing.
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      #onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.#isOpen) return;
        this.#close();
        this.#trigger.focus();
      };

      #onPanelTransitionEnd = (event) => {
        if (event.target !== this.#panel || event.propertyName !== 'opacity') return;
        if (!this.#panel.classList.contains('is-open')) this.#panel.hidden = true;
        this.#panel.removeEventListener('transitionend', this.#onPanelTransitionEnd);
      };

      #onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.#close();
      };
    }
  );
}
